import { useEffect, useState } from "react";
import React from "react";
import HeaderPageBrad from "../../components/ComponentsPageBrad/HeaderPageBrad";
import HeaderPageLogin from "../../components/ComponentsPageBrad/HeaderPageLogin";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import getThemeColors from "../../utils/getColorTheme";
import { useTheme, ThemeContext } from "../../contexts/ThemeContext";
import logoDark from "../../assets/images/icons/logo-dark.svg";
import logoLight from "../../assets/images/icons/logoLight.svg";

// Light theme provider for Terms and Conditions page to match Figma design
const LightThemeProvider = ({ children }) => {
  const originalContext = useTheme();

  // Force the background to be white
  const whiteThemeValue = {
    addTheme: 'white',
    setAddTheme: originalContext.setAddTheme,
    clearWithTheme: originalContext.clearWithTheme
  };

  return (
    <ThemeContext.Provider value={whiteThemeValue}>
      {children}
    </ThemeContext.Provider>
  );
};

const TermsAndConditionsContent = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const supabaseClient = useSupabaseClient();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 992;
      setIsMobile(mobile);
    };

    handleResize();

    const checkAuthStatus = async () => {
      const { data: { session } } = await supabaseClient.auth.getSession();
      setIsLoggedIn(!!session?.user);
    };
    
    checkAuthStatus();

    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange((event, session) => {
      setIsLoggedIn(!!session?.user);
    });

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      subscription.unsubscribe();
    };
  }, [supabaseClient]);

  // Typography styles matching Figma design
  const headingOneStyle = {
    fontFamily: "Plus Jakarta Sans",
    fontWeight: 700,
    fontSize: 56,
    fontStyle: "normal",
    lineHeight: "110%",
    letterSpacing: "-1.12px",
    color: "#16140E",
    textAlign: isMobile ? "left" : "center",
    margin: "0 0 24px 0",
  };

  const headingTwoStyle = {
    fontFamily: "Plus Jakarta Sans",
    fontWeight: 700,
    fontSize: 24,
    fontStyle: "normal",
    lineHeight: "130%",
    letterSpacing: "-0.48px",
    color: "#16140E",
    textAlign: "left",
    margin: "48px 0 24px 0",
  };

  const headingThreeStyle = {
    fontFamily: "Plus Jakarta Sans",
    fontWeight: 700,
    fontSize: 18,
    fontStyle: "normal",
    lineHeight: "150%",
    letterSpacing: "-0.36px",
    color: "#16140E",
    margin: "32px 0 16px 0",
  };

  const bodyStyle = {
    fontFamily: "Plus Jakarta Sans",
    fontWeight: 500,
    fontSize: 16,
    lineHeight: "150%",
    color: "#16140E",
    margin: "0 0 16px 0",
  };

  const captionStyle = {
    fontFamily: "Plus Jakarta Sans",
    fontWeight: 500,
    fontSize: 14,
    fontStyle: "italic",
    lineHeight: "140%",
    color: "#4D4D4D",
    textAlign: "left",
  };


  // Table styles matching Figma design
  const tableHeaderStyle = {
    backgroundColor: "#16140E",
    color: "#FFFFFF",
    padding: "16px 24px",
    fontFamily: "Plus Jakarta Sans",
    fontWeight: 700,
    fontSize: 18,
    lineHeight: "150%",
    letterSpacing: "-0.36px",
  };

  const tableCellStyle = {  
    backgroundColor: "#FFFFFF",
    color: "#16140E",
    padding: "24px",
    fontFamily: "Plus Jakarta Sans",
    fontWeight: 500,
    fontSize: 16,
    lineHeight: "150%",
    verticalAlign: "top",
  };

  const listStyle = {
    ...bodyStyle,
    paddingLeft: 0,
    marginBottom: 32,
    listStyle: 'none'
  };

  const listItemStyle = {
    marginBottom: 12,
    paddingLeft: 20,
    position: 'relative'
  };

  const bulletStyle = {
    position: 'absolute',
    left: 0,
    top: 0,
    fontWeight: 'bold'
  };

  return (
    <div>
      <style>{`
        html, body {
          overflow: auto !important;
          height: auto !important;
          max-height: none !important;
        }
        
        .section-main {
          height: auto !important;
          overflow: visible !important;
        }
      `}</style>
      
      <section
        className="section-main"
        style={{ background: "#FFFFFF", minHeight: "100vh" }}
      >
        {isLoggedIn ? <HeaderPageBrad openModalLogin={""} /> : <HeaderPageLogin />}
        
        <div style={{ 
          width: "100%",
          minHeight: "100vh",
          paddingBottom: isMobile ? "34px" : "40px",
        }}>
          <div style={{ 
            maxWidth: 1440, 
            margin: "0 auto", 
            padding: isMobile ? "72px 16px 0" : "88px 80px 0",
            width: "100%",
            boxSizing: "border-box"
          }}>
            
            {/* Header Section */}
            <section style={{ 
              marginBottom: isMobile ? 40 : 60,
              paddingTop: isMobile ? 0 : 40,
            }}>
              <h1 style={headingOneStyle}>
                Terms & Conditions
              </h1>
            </section>

            {/* Content Section */}
            <section style={{ 
              marginBottom: isMobile ? 80 : 100,
              maxWidth: "680px",
              marginLeft: "auto",
              marginRight: "auto",
              padding: isMobile ? "0 16px" : "0",
            }}>

              <h2 style={headingTwoStyle}>1. Acceptance of Terms</h2>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>1.1</strong> By using the FAM Website and FAM App (FAM), you agree to be bound by these Terms and Conditions. FAM is operated by Provider Choice Shop Pty Ltd (ABN 22 678 898 265) trading as FAM Shop (<strong>we, us, or our</strong>).
              </p>

              <h2 style={headingTwoStyle}>2. Use of FAM</h2>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>2.1</strong> FAM is designed to assist National Disability Insurance Scheme (NDIS) participants in understanding their plans. Information provided by FAM is general in nature and should not be considered definitive or comprehensive.
              </p>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>2.2</strong> You agree to use FAM only for lawful purposes and in accordance with these Terms.
              </p>

              <h2 style={headingTwoStyle}>3. User Obligations</h2>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>3.1</strong> You agree to provide accurate and complete information when using FAM.
              </p>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>3.2</strong> You warrant that:
              </p>
              <p style={{ ...bodyStyle, paddingLeft: 40 }}>
                <strong>(a)</strong> you have all necessary rights and consents to upload and use any NDIS plan or other information.
              </p>
              <p style={{ ...bodyStyle, paddingLeft: 40 }}>
                <strong>(b)</strong> you are either the NDIS participant or have explicit permission from the participant to use their information.
              </p>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>3.3</strong> If you are a support coordinator, informal support, or any person other than the NDIS participant, you warrant that you have obtained explicit consent from the NDIS participant to upload and use their plan information in FAM. You must confirm this by ticking the relevant box before uploading any plan information.
              </p>

              <h2 style={headingTwoStyle}>4. Privacy and Data Protection</h2>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>4.1</strong> We collect and process personal information in accordance with Privacy Policy and the <em>Privacy Act 1988</em> (Cth) including the Australian Privacy Principles.
              </p>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>4.2</strong> While we take measures to de-identify sensitive information, you acknowledge that uploading your NDIS plan involves the initial capture of sensitive data.
              </p>

              <h2 style={headingTwoStyle}>5. Limitation of Liability</h2>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>5.1</strong> FAM provides information of a general nature only. You should always seek confirmation from your Local Area Coordinator (LAC) or the NDIS or NDIA for specific advice.
              </p>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>5.2</strong> To the fullest extent permitted by law, we exclude all warranties, representations, and guarantees relating to FAM.
              </p>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>5.3</strong> Subject to your consumer law rights, our total liability under this Agreement and conditions is limited to AU$100.00.
              </p>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>5.4</strong> We are not liable for any indirect or consequential losses that may arise from your use of FAM.
              </p>

              <h2 style={headingTwoStyle}>6. Changes to Terms</h2>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>6.1</strong> We reserve the right to modify these Terms at any time. Continued use of FAM after any changes constitutes acceptance of the new Terms.
              </p>

              <h2 style={headingTwoStyle}>7. Governing Law and Dispute Resolution</h2>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>7.1</strong> These Terms are governed by the laws of New South Wales, Australia.
              </p>
              <p style={{ ...bodyStyle, paddingLeft: 20 }}>
                <strong>7.2</strong> In the event of a dispute, the parties will first attempt to resolve the matter through mutual discussion or mediation.
              </p>

              

              <p style={bodyStyle}>
                By using FAM, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>

              

              <h2 style={headingTwoStyle}>Contact Information</h2>
              <p style={bodyStyle}>
                If you have any questions about these Terms and Conditions, please contact us:
              </p>
              
              <div style={{ 
                borderRadius: 8,
                overflow: "hidden",
                border: "1px solid #16140E",
                marginTop: 24,
                marginBottom: 48
              }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ ...tableHeaderStyle, borderRight: "1px solid #FFFED9" }}>
                        Contact Method
                      </th>
                      <th style={tableHeaderStyle}>
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ ...tableCellStyle, borderRight: "1px solid #16140E", borderBottom: "1px solid #16140E" }}>
                        Phone
                      </td>
                      <td style={{ ...tableCellStyle, borderBottom: "1px solid #16140E" }}>
                        1300 776 246<br/><br/>
                        <em>You can contact us from 9 am to 5 pm, Monday to Friday AEST</em>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ ...tableCellStyle, borderRight: "1px solid #16140E", borderBottom: "1px solid #16140E" }}>
                        Email
                      </td>
                      <td style={{ ...tableCellStyle, borderBottom: "1px solid #16140E" }}>
                        info@fam.life
                      </td>
                    </tr>
                    <tr>
                      <td style={{ ...tableCellStyle, borderRight: "1px solid #16140E" }}>
                        Postal address
                      </td>
                      <td style={tableCellStyle}>
                        418A Elizabeth St, Surry Hills NSW 2010
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </section>

            {/* Footer */}
            <footer style={{padding: isMobile ? "80px 0 0 0" : "40px 0 0 0", textAlign: "center"}}>
              <hr style={{ border: 'none', borderTop: '1px solid #DEDEE1', margin: isMobile ? '0 0 24px 0' : '0 0 60px 0' }} />
              <div style={{ marginBottom: 0 }}>
                <img src={logoDark} alt="Logo" style={{ width: 80, height: 28, margin: "0 auto 0px" }} />
              </div>
              
              <hr style={{ border: 'none', borderTop: '1px solid #DEDEE1', margin: isMobile ? '24px 0 24px 0' : '60px 0 60px 0' }} />

              {isMobile ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center", alignSelf: "stretch" }}>
                  <p style={{ fontFamily: "Inter", fontSize: 14, color: "#16140E", margin: 0, lineHeight: "21px", textAlign: "center", padding: "0 16px" }}>© 2025 For All Milestones. Registered Provider no. 4-433C-3380.</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center", alignSelf: "stretch" }}>
                    <a href="/terms" style={{ textDecoration: 'none' }}><span style={{ fontFamily: "Inter", fontSize: 12, color: "#16140E", cursor: 'pointer', lineHeight: "21px", textAlign: "center" }}>Privacy Policy</span></a>
                    <a href="/terms" style={{ textDecoration: 'none' }}><span style={{ fontFamily: "Inter", fontSize: 12, color: "#16140E", cursor: 'pointer', lineHeight: "21px", textAlign: "center" }}>Terms & Conditions</span></a>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <p
                    style={{
                      fontFamily: "Inter",
                      fontSize: 14,
                      color: "#16140E",
                      margin: 0,
                    }}
                  >
                    © 2025 For All Milestones. Registered Provider no. 4-433C-3380.
                  </p>
                  <div style={{ display: "flex", gap: 32 }}>
                    <a href="/terms" style={{ textDecoration: 'none' }}><span style={{ fontFamily: "Inter", fontSize: 14, color: "#16140E", cursor: 'pointer' }}>Privacy Policy</span></a>
                    <a href="/terms" style={{ textDecoration: 'none' }}><span style={{ fontFamily: "Inter", fontSize: 14, color: "#16140E", cursor: 'pointer' }}>Terms & Conditions</span></a>
                  </div>
                </div>
              )}
            </footer>
          </div>
        </div>
      </section>
    </div>
  );
};

const TermsAndConditionsPage = () => {
  return (
    <LightThemeProvider>
      <TermsAndConditionsContent />
    </LightThemeProvider>
  );
};

export default TermsAndConditionsPage; 